<template>
  <div v-if="content.historicalData" class="first:mt-box-2 last:mb-box-2">
    <HighlightBox
      v-if="uniqueProviders.length > 1"
      type="recommendation"
      class="mx-box-2 my-12"
      responsive
    >
      <SliceTitle class="subtitle1" :value="title" />
      <div class="typography">
        <ul class="mt-6">
          <li class="font-semibold" v-for="(bulletPoint, key) in bulletPoints" :key="key">
            <span>{{ bulletPoint.label }}:</span>
            <template v-for="(provider, index) in bulletPoint.providers" :key="index">
              <template v-if="index > 0">{{ $t('and') }}</template>
              <a
                :href="addAffiliateMobileAppParams(provider.affiliateUrl)"
                rel="nofollow noopener"
                target="_blank"
                >{{ provider.displayName }}</a
              >
              <template v-if="bulletPoint.type === 'BESTSCORE' && provider.score">
                &nbsp;({{ provider.score.value }}/10)
              </template>
            </template>
          </li>
        </ul>
      </div>
    </HighlightBox>
    <HighlightBox
      v-else-if="uniqueProviders.length === 1"
      type="recommendation"
      class="mx-box-2 my-12"
      responsive
    >
      <p class="typography font-semibold" v-html="oneProviderToRecommend" />
    </HighlightBox>
    <HighlightBox v-else type="warning" class="mx-box-2 my-12" responsive>
      <p class="typography font-semibold">
        {{
          $t('best_providers_for.no_providers_to_recommend', {
            from: content.historicalData.countryFrom?.labels.from ?? '',
            to: content.historicalData.countryTo.labels.to ?? '',
          })
        }}
      </p>
    </HighlightBox>
  </div>
</template>

<script lang="ts" setup>
import { type BulletPoint, createBulletPoints } from './create-bullet-points'
import type { GQLBestProvidersForSlice } from './generated/best-providers-for'

const MAX_BULLET_POINTS = 7

const props = defineProps<{ content: GQLBestProvidersForSlice }>()

const { $t } = useNuxtApp()
const logger = useLogger('best-providers-for')

const title = computed<NonNullable<GQLBestProvidersForSlice['optionalTitle']>>(() => {
  if (!props.content.optionalTitle?.text) {
    return {
      style: 'h2',
      text: $t('best_providers_for.best_providers_to_send_money', {
        from: props.content.historicalData?.countryFrom?.labels.from ?? '',
        to: props.content.historicalData?.countryTo.labels.to,
      }),
    }
  }
  return props.content.optionalTitle
})

const uniqueProviders = computed<string[]>(() => {
  const providers = bulletPoints.value.flatMap((bullet) =>
    bullet.providers.map((p) => p.displayName),
  )
  return [...new Set(providers)]
})

const oneProviderToRecommend = computed(() => {
  return $t('best_providers_for.one_provider_to_recommend', {
    from: props.content.historicalData?.countryFrom?.labels.from ?? '',
    to: props.content.historicalData?.countryTo.labels.to,
    provider: `<a class='link' href='${addAffiliateMobileAppParams(
      bulletPoints.value[0].providers[0].affiliateUrl,
    )}'> ${uniqueProviders.value[0]} </a>`,
  })
})

const bulletPoints = computed((): BulletPoint[] => {
  if (!props.content.historicalData) {
    return []
  }
  try {
    const bulletPoints = createBulletPoints({
      historicalData: props.content.historicalData,
      bestCashPayoutProvider: props.content.bestCashPayoutProvider,
      $t,
    })
    return bulletPoints.slice(0, MAX_BULLET_POINTS)
  } catch (error) {
    logger.error(error, 'Failed to create bullet points')
    return []
  }
})
</script>
